import React from "react";
import { useFullScreenHandle } from "react-full-screen";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";

import { kioskApi } from "../api";

const DEVICE_STORAGE_KEY = "kiosk-device-id";

function getStoredDeviceId(venueId: string): string | null {
	try {
		const stored = localStorage.getItem(`${DEVICE_STORAGE_KEY}:${venueId}`);
		return stored || null;
	} catch {
		return null;
	}
}

function storeDeviceId(venueId: string, deviceId: string | null) {
	try {
		if (deviceId) {
			localStorage.setItem(`${DEVICE_STORAGE_KEY}:${venueId}`, deviceId);
		} else {
			localStorage.removeItem(`${DEVICE_STORAGE_KEY}:${venueId}`);
		}
	} catch {
		// localStorage not available
	}
}

export function useKioskOverview({
	venueId,
	tenant,
	timeZone,
}: {
	venueId: string;
	tenant: string;
	timeZone: string;
}) {
	const data = useSuspenseQuery({
		...kioskApi.getOptions(tenant, venueId),
		refetchInterval: 10_000,
	});

	const [showCheckInOut, setShowCheckInOut] = React.useState(false);
	const [currentUserId, setCurrentUserId] = React.useState<
		string | null | undefined
	>();
	const [deviceId, setDeviceIdState] = React.useState<string | null>(() =>
		getStoredDeviceId(venueId),
	);

	const setDeviceId = React.useCallback(
		(id: string | null) => {
			setDeviceIdState(id);
			storeDeviceId(venueId, id);
		},
		[venueId],
	);

	const userOperationsQuery = useQuery({
		...kioskApi.getUserOperationsOptions(tenant, venueId, currentUserId),
	});

	const handle = useFullScreenHandle();

	return {
		...data.data,
		timeZone,
		venueId,
		tenant,
		handle,
		showCheckInOut,
		setShowCheckInOut,

		currentUserId,
		setCurrentUserId: (id: string | null | undefined) => {
			setCurrentUserId(id);
		},
		currentUserOperations: userOperationsQuery.data ?? [],
		isLoadingUserOperations: userOperationsQuery.isLoading,

		deviceId,
		setDeviceId,
	};
}
